// /admin/claims — review and approve/reject business ownership claims
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Clock, Building2, User } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminClaims() {
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-claims', statusFilter],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/claims?status=${statusFilter}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load');
      return res.json() as Promise<{ claims: any[] }>;
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (claimId: string) => {
      const res = await fetch(`${API_URL}/api/admin/claims/${claimId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-claims'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: async (claimId: string) => {
      const res = await fetch(`${API_URL}/api/admin/claims/${claimId}/reject`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-claims'] }),
  });

  const claims = data?.claims ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Business Claims</h1>
        <div className="flex gap-2">
          {(['pending', 'approved', 'rejected'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-[#C8102E] text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : claims.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          No {statusFilter} claims
        </div>
      ) : (
        <div className="space-y-3">
          {claims.map((claim: any) => (
            <div
              key={claim.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold text-gray-900 dark:text-white text-sm">
                      {claim.place_name}
                    </span>
                    <span className="text-xs text-gray-500">{claim.city}, {claim.country}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 capitalize">
                      {claim.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {claim.user_email}
                    </span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-500 capitalize">{claim.business_role}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-500">
                      {new Date(claim.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {claim.verification_notes && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 pl-6">
                      "{claim.verification_notes}"
                    </p>
                  )}
                  {claim.owner_user_id && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 pl-6">
                      ⚠ Place already has an owner
                    </p>
                  )}
                </div>

                {statusFilter === 'pending' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => rejectMutation.mutate(claim.id)}
                      disabled={rejectMutation.isPending || approveMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => approveMutation.mutate(claim.id)}
                      disabled={approveMutation.isPending || rejectMutation.isPending || !!claim.owner_user_id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
